import React, { useState } from 'react';
import { Plus, Trash2, Check, X, Youtube, Search, Link2, Unlink, Target, Clock, Layers, Timer, Loader2, Dumbbell, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { ExercisePickerModal } from './ExercisePickerModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const REST_PRESETS = [30, 45, 60, 90, 120, 180];
export const GOAL_SUGGESTIONS = [
  'Prise de muscle', 'Force pure', 'Perte de poids / recomposition', 'Powerbuilding',
  'Spécial fessiers / bas du corps', 'Spécial haut du corps', 'Remise en forme générale',
  'Préparation physique générale', 'Renforcement post-blessure',
];

export const blankExercise = () => ({
  exercise_id: null, name: '', muscle_group: '', set_type: 'straight', group_id: null, reps_scheme: '8-12', is_time_based: false,
  sets_count: 3, rest_seconds: 90, tempo: '', youtube_url: '', notes: '',
});
export const blankSession = (n) => ({ session_label: `Séance ${n}`, sessions_target: 12, exercises: [blankExercise()] });
export const blankProgram = () => ({
  name: '', goal: '', weeks_target: 12, tonnage_growth_target_pct: 5,
  sessions: [blankSession(1)],
});

const newGroupId = () => `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** Regroupe les index d'exercices consécutifs partageant le même group_id en blocs. */
const blocksOf = (exercises) => {
  const out = [];
  exercises.forEach((ex, idx) => {
    const last = out[out.length - 1];
    if (ex.group_id && last && last.groupId === ex.group_id) last.indices.push(idx);
    else out.push({ groupId: ex.group_id || null, indices: [idx] });
  });
  return out;
};

/** Formulaire d'édition de programme (séances + exercices), partagé entre les programmes
 * clients (ProgramsProEditor) et les brouillons IA (AIWorkoutDraftsPanel) — coach uniquement.
 * Chaque exercice peut être entièrement rempli par l'IA à partir de son seul nom (groupe
 * musculaire, séries/reps/repos/tempo, vidéo YouTube, illustration réutilisée depuis la
 * librairie ou générée + sauvegardée automatiquement). */
export const ProgramEditorForm = ({
  editing, upd, library, setTypes, csrfToken, onSave, onCancel, busy,
  pendingRequestBanner = null, testIdPrefix = '',
}) => {
  const [videoSearch, setVideoSearch] = useState(null); // { sIdx, eIdx, results, loading }
  const [pickerFor, setPickerFor] = useState(null); // { sIdx, eIdx }

  const fetchWithCsrf = (url, options = {}) => fetch(url, {
    ...options, credentials: 'include',
    headers: { ...options.headers, 'X-CSRF-Token': csrfToken },
  });

  const byGroup = (group) => library.exercises.filter((e) => !group || e.muscle_group === group);
  const nameToGroup = (name) => library.exercises.find((e) => e.name.toLowerCase() === (name || '').trim().toLowerCase())?.muscle_group;

  const SOLO_TYPES = setTypes.filter((t) => !t.group);
  const GROUP_TYPES = setTypes.filter((t) => t.group);

  const linkWithNext = (sIdx, eIdx, groupSetType) => {
    upd((c) => {
      const exs = c.sessions[sIdx].exercises;
      const cur = exs[eIdx];
      const gid = cur.group_id || newGroupId();
      cur.group_id = gid; cur.set_type = groupSetType;
      const next = exs[eIdx + 1];
      if (next && !next.group_id) { next.group_id = gid; next.set_type = groupSetType; }
      else { exs.splice(eIdx + 1, 0, { ...blankExercise(), group_id: gid, set_type: groupSetType, rest_seconds: cur.rest_seconds }); }
    });
  };
  const addExerciseToGroup = (sIdx, afterIdx, groupId, groupSetType, restSeconds) => {
    upd((c) => { c.sessions[sIdx].exercises.splice(afterIdx + 1, 0, { ...blankExercise(), group_id: groupId, set_type: groupSetType, rest_seconds: restSeconds }); });
  };
  const unlinkGroup = (sIdx, groupId) => {
    upd((c) => { c.sessions[sIdx].exercises.forEach((ex) => { if (ex.group_id === groupId) { ex.group_id = null; ex.set_type = 'straight'; } }); });
  };
  const setGroupType = (sIdx, groupId, newType) => {
    upd((c) => { c.sessions[sIdx].exercises.forEach((ex) => { if (ex.group_id === groupId) ex.set_type = newType; }); });
  };
  const setGroupRest = (sIdx, groupId, seconds) => {
    upd((c) => { c.sessions[sIdx].exercises.forEach((ex) => { if (ex.group_id === groupId) ex.rest_seconds = seconds; }); });
  };

  const searchVideo = (sIdx, eIdx, query) => {
    if (!query || query.trim().length < 2) { toast.error("Tapez d'abord le nom de l'exercice."); return; }
    setVideoSearch({ sIdx, eIdx, results: [], loading: true });
    fetch(`${BACKEND_URL}/api/admin/programs-pro/youtube-search?q=${encodeURIComponent(query)}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setVideoSearch({ sIdx, eIdx, results: d.results || [], loading: false }))
      .catch(() => setVideoSearch({ sIdx, eIdx, results: [], loading: false }));
  };

  const pickVideo = (sIdx, eIdx, url) => {
    upd((c) => { c.sessions[sIdx].exercises[eIdx].youtube_url = url; });
    setVideoSearch(null);
  };

  const applyLibraryExercise = (sIdx, eIdx, ex) => {
    upd((c) => {
      const target = c.sessions[sIdx].exercises[eIdx];
      target.name = ex.name_fr;
      target.muscle_group = ex.muscle_group;
      target.image_url = ex.image_url || target.image_url;
      if (ex.youtube_url) target.youtube_url = ex.youtube_url;
      if (ex.tempo_default && !target.tempo) target.tempo = ex.tempo_default;
      if (ex.rest_default_sec) target.rest_seconds = ex.rest_default_sec;
      if (ex.tips && ex.tips.length && !target.notes) target.notes = ex.tips[0];
      if (ex.type === 'cardio') target.is_time_based = true;
    });
    setPickerFor(null);
    toast.success(`"${ex.name_fr}" ajouté depuis la bibliothèque.`);
  };


  return (
    <div className="space-y-5" data-testid={`${testIdPrefix}program-form`}>
      {pendingRequestBanner}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-zinc-500 mb-1 block">Nom du programme</label>
          <input value={editing.name} onChange={(e) => upd((c) => { c.name = e.target.value; })}
            className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" placeholder="Ex: Force & Hypertrophie" data-testid={`${testIdPrefix}program-name-input`} />
        </div>
        <div>
          <label className="text-xs text-zinc-500 mb-1 block">Objectif du programme</label>
          <input value={editing.goal} onChange={(e) => upd((c) => { c.goal = e.target.value; })}
            list={`${testIdPrefix}goal-suggestions`} className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" placeholder="Ex: Prise de muscle" data-testid={`${testIdPrefix}program-goal-input`} />
          <datalist id={`${testIdPrefix}goal-suggestions`}>{GOAL_SUGGESTIONS.map((g) => <option key={g} value={g} />)}</datalist>
        </div>
        <div>
          <label className="text-xs text-zinc-500 mb-1 block">Durée cible (semaines)</label>
          <input type="number" min={1} max={104} value={editing.weeks_target} onChange={(e) => upd((c) => { c.weeks_target = +e.target.value; })}
            className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid={`${testIdPrefix}program-weeks-input`} />
        </div>
        <div>
          <label className="text-xs text-zinc-500 mb-1 block">Objectif progression tonnage (%)</label>
          <input type="number" min={0} max={100} step={0.5} value={editing.tonnage_growth_target_pct} onChange={(e) => upd((c) => { c.tonnage_growth_target_pct = +e.target.value; })}
            className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid={`${testIdPrefix}program-tonnage-target-input`} />
        </div>
      </div>

      {editing.sessions.map((s, sIdx) => (
        <div key={sIdx} className="border border-white/10 rounded-xl p-4 bg-black/30" data-testid={`${testIdPrefix}session-block-${sIdx}`}>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <input value={s.session_label} onChange={(e) => upd((c) => { c.sessions[sIdx].session_label = e.target.value; })}
              className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm font-bold w-40" data-testid={`${testIdPrefix}session-label-input-${sIdx}`} />
            <label className="text-xs text-zinc-500 flex items-center gap-1.5">
              <Target className="w-3.5 h-3.5" /> Nb séances cible :
              <input type="number" min={1} max={300} value={s.sessions_target} onChange={(e) => upd((c) => { c.sessions[sIdx].sessions_target = +e.target.value; })}
                className="w-16 bg-zinc-800 border border-white/10 rounded-lg px-2 py-1 text-white text-sm" data-testid={`${testIdPrefix}session-target-input-${sIdx}`} />
            </label>
            {editing.sessions.length > 1 && (
              <button onClick={() => upd((c) => { c.sessions.splice(sIdx, 1); })} className="ml-auto text-red-400 hover:text-red-300 text-xs inline-flex items-center gap-1" data-testid={`${testIdPrefix}delete-session-${sIdx}`}>
                <Trash2 className="w-3.5 h-3.5" /> Supprimer la séance
              </button>
            )}
          </div>

          <div className="space-y-3">
            {blocksOf(s.exercises).map((block) => {
              const renderFields = (eIdx, ex, showTypeRest) => {
                return (
                  <div key={eIdx} className="space-y-2" data-testid={`${testIdPrefix}exercise-row-${sIdx}-${eIdx}`}>
                    <div className="flex items-center gap-2">
                      {ex.image_url ? (
                        <img src={`${BACKEND_URL}${ex.image_url}`} alt={ex.name} loading="lazy"
                          className="w-10 h-10 rounded-lg object-contain border border-white/10 flex-shrink-0 bg-zinc-800"
                          data-testid={`${testIdPrefix}exercise-image-${sIdx}-${eIdx}`} />
                      ) : (
                        <div className="w-10 h-10 rounded-lg border border-white/10 bg-zinc-800 flex items-center justify-center flex-shrink-0" data-testid={`${testIdPrefix}exercise-image-placeholder-${sIdx}-${eIdx}`}>
                          <Dumbbell className="w-4 h-4 text-zinc-600" />
                        </div>
                      )}
                      <input value={ex.name}
                        onChange={(e) => upd((c) => {
                          c.sessions[sIdx].exercises[eIdx].name = e.target.value;
                          const g = nameToGroup(e.target.value);
                          if (g) c.sessions[sIdx].exercises[eIdx].muscle_group = g;
                        })}
                        onBlur={(e) => {
                          const val = e.target.value.trim();
                          if (!val || nameToGroup(val) || ex.muscle_group) return;
                          fetch(`${BACKEND_URL}/api/admin/programs-pro/detect-muscle-group?name=${encodeURIComponent(val)}`, { credentials: 'include' })
                            .then((r) => r.json())
                            .then((d) => { if (d.muscle_group) upd((c) => { c.sessions[sIdx].exercises[eIdx].muscle_group = d.muscle_group; }); })
                            .catch(() => {});
                        }}
                        list={`${testIdPrefix}exercise-lib-${sIdx}-${eIdx}`}
                        title="Nom de l'exercice (recherche dans la base ou libre — détection auto du groupe)"
                        placeholder="Nom de l'exercice" className="flex-1 min-w-0 bg-zinc-800 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm" data-testid={`${testIdPrefix}exercise-name-input-${sIdx}-${eIdx}`} />
                      <datalist id={`${testIdPrefix}exercise-lib-${sIdx}-${eIdx}`}>
                        {byGroup(ex.muscle_group).map((lib) => <option key={lib.exercise_ref_id} value={lib.name} />)}
                      </datalist>
                      <button onClick={() => upd((c) => { c.sessions[sIdx].exercises.splice(eIdx, 1); })} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors flex-shrink-0" data-testid={`${testIdPrefix}delete-exercise-${sIdx}-${eIdx}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <select value={ex.muscle_group || ''} onChange={(e) => upd((c) => { c.sessions[sIdx].exercises[eIdx].muscle_group = e.target.value; })}
                        className="bg-zinc-800 border border-white/10 rounded-lg px-2 py-1.5 text-zinc-300 text-xs flex-1 min-w-[110px] sm:flex-none sm:w-36" data-testid={`${testIdPrefix}muscle-group-select-${sIdx}-${eIdx}`}>
                        <option value="">Groupe musculaire</option>
                        {library.muscle_groups.map((g) => <option key={g} value={g}>{g}</option>)}
                      </select>
                      <button onClick={() => setPickerFor({ sIdx, eIdx })}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-violet-600/20 text-violet-300 rounded-lg text-xs font-bold hover:bg-violet-600/30 transition-colors" title="Choisir dans la bibliothèque ou générer un nouvel exercice par IA (texte, image, vidéo Sora 2)" data-testid={`${testIdPrefix}browse-library-btn-${sIdx}-${eIdx}`}>
                        <Wand2 className="w-3.5 h-3.5" /> Choisir / IA
                      </button>
                      <button onClick={() => searchVideo(sIdx, eIdx, ex.name)} className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-600/20 text-red-400 rounded-lg text-xs font-semibold hover:bg-red-600/30 transition-colors" title="Rechercher une vidéo YouTube existante pour cet exercice" data-testid={`${testIdPrefix}search-video-btn-${sIdx}-${eIdx}`}>
                        <Search className="w-3.5 h-3.5" /> Vidéo
                      </button>
                      {!ex.group_id && (
                        <div className="relative">
                          <select value="" onChange={(e) => { if (e.target.value) linkWithNext(sIdx, eIdx, e.target.value); }}
                            title="Lier cet exercice au suivant (superset, bi-set...)"
                            className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-2 py-1.5 text-amber-300 text-xs font-semibold" data-testid={`${testIdPrefix}link-group-select-${sIdx}-${eIdx}`}>
                            <option value="">🔗 Lier au suivant...</option>
                            {GROUP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                      )}
                    </div>

                    {videoSearch && videoSearch.sIdx === sIdx && videoSearch.eIdx === eIdx && (
                      <div className="bg-black/50 border border-red-500/20 rounded-lg p-2 space-y-1.5" data-testid={`${testIdPrefix}video-results-${sIdx}-${eIdx}`}>
                        {videoSearch.loading && <p className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Recherche en cours...</p>}
                        {!videoSearch.loading && videoSearch.results.length === 0 && <p className="text-xs text-zinc-500">Aucun résultat (quota vidéo YouTube peut-être épuisé aujourd'hui). Ajoutez un lien manuellement ci-dessous.</p>}
                        {videoSearch.results.map((v) => (
                          <button key={v.video_id} onClick={() => pickVideo(sIdx, eIdx, v.url)} className="w-full flex items-center gap-2 text-left hover:bg-white/5 rounded p-1.5 transition-colors" data-testid={`${testIdPrefix}video-result-${v.video_id}`}>
                            <img src={v.thumbnail} alt="" className="w-14 h-10 rounded object-cover flex-shrink-0" />
                            <span className="text-xs text-zinc-300 line-clamp-2">{v.title} <span className="text-zinc-600">· {v.channel}</span></span>
                          </button>
                        ))}
                        <button onClick={() => setVideoSearch(null)} className="text-xs text-zinc-500 hover:text-white">Fermer</button>
                      </div>
                    )}

                    <div className={`grid grid-cols-2 ${showTypeRest ? 'sm:grid-cols-5' : 'sm:grid-cols-3'} gap-2`}>
                      {showTypeRest && (
                        <div>
                          <label className="text-[10px] text-zinc-500 flex items-center gap-1"><Layers className="w-3 h-3" /> Type de série</label>
                          <select value={ex.set_type} onChange={(e) => upd((c) => { c.sessions[sIdx].exercises[eIdx].set_type = e.target.value; })}
                            className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs" data-testid={`${testIdPrefix}set-type-select-${sIdx}-${eIdx}`}>
                            {SOLO_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="text-[10px] text-zinc-500 flex items-center gap-1">
                          {ex.is_time_based ? 'Temps' : 'Répétitions'}
                          <input type="checkbox" checked={ex.is_time_based} onChange={(e) => upd((c) => { c.sessions[sIdx].exercises[eIdx].is_time_based = e.target.checked; })}
                            className="w-3 h-3 accent-primary" title="Basé sur le temps (cardio/isométrique)" data-testid={`${testIdPrefix}time-based-checkbox-${sIdx}-${eIdx}`} />
                        </label>
                        <input value={ex.reps_scheme} onChange={(e) => upd((c) => { c.sessions[sIdx].exercises[eIdx].reps_scheme = e.target.value; })}
                          placeholder={ex.is_time_based ? '30s' : '8-12'} className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs" data-testid={`${testIdPrefix}reps-scheme-input-${sIdx}-${eIdx}`} />
                      </div>
                      <div>
                        <label className="text-[10px] text-zinc-500">Nb séries</label>
                        <div className="flex items-center gap-1">
                          <button onClick={() => upd((c) => { c.sessions[sIdx].exercises[eIdx].sets_count = Math.max(1, c.sessions[sIdx].exercises[eIdx].sets_count - 1); })} className="px-2 py-1.5 bg-zinc-800 border border-white/10 rounded-lg text-white text-xs" data-testid={`${testIdPrefix}sets-count-minus-${sIdx}-${eIdx}`}>−</button>
                          <span className="flex-1 text-center text-white text-sm font-bold" data-testid={`${testIdPrefix}sets-count-value-${sIdx}-${eIdx}`}>{ex.sets_count}</span>
                          <button onClick={() => upd((c) => { c.sessions[sIdx].exercises[eIdx].sets_count += 1; })} className="px-2 py-1.5 bg-zinc-800 border border-white/10 rounded-lg text-white text-xs" data-testid={`${testIdPrefix}sets-count-plus-${sIdx}-${eIdx}`}>+</button>
                        </div>
                      </div>
                      {showTypeRest && (
                        <div>
                          <label className="text-[10px] text-zinc-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Récup (s)</label>
                          <select value={ex.rest_seconds} onChange={(e) => upd((c) => { c.sessions[sIdx].exercises[eIdx].rest_seconds = +e.target.value; })}
                            className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs" data-testid={`${testIdPrefix}rest-seconds-select-${sIdx}-${eIdx}`}>
                            {REST_PRESETS.map((r) => <option key={r} value={r}>{r}s</option>)}
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="text-[10px] text-zinc-500 flex items-center gap-1"><Timer className="w-3 h-3" /> Tempo</label>
                        <input value={ex.tempo || ''} onChange={(e) => upd((c) => { c.sessions[sIdx].exercises[eIdx].tempo = e.target.value; })}
                          placeholder="3-1-2-0" className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs" data-testid={`${testIdPrefix}tempo-input-${sIdx}-${eIdx}`} />
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Link2 className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
                      <input value={ex.youtube_url || ''} onChange={(e) => upd((c) => { c.sessions[sIdx].exercises[eIdx].youtube_url = e.target.value; })}
                        placeholder="Lien YouTube (auto ou manuel)" className="flex-1 bg-zinc-800 border border-white/10 rounded-lg px-2 py-1.5 text-zinc-300 text-xs" data-testid={`${testIdPrefix}youtube-url-input-${sIdx}-${eIdx}`} />
                      {ex.youtube_url && <a href={ex.youtube_url} target="_blank" rel="noreferrer" className="text-red-400"><Youtube className="w-4 h-4" /></a>}
                    </div>
                    <input value={ex.notes || ''} onChange={(e) => upd((c) => { c.sessions[sIdx].exercises[eIdx].notes = e.target.value; })}
                      placeholder="Note technique (ex: garder le dos droit, contrôler la descente...)"
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2 py-1.5 text-zinc-300 text-xs" data-testid={`${testIdPrefix}notes-input-${sIdx}-${eIdx}`} />
                  </div>
                );
              };

              if (block.indices.length === 1) {
                const eIdx = block.indices[0];
                return (
                  <div key={eIdx} className="bg-zinc-900/70 border border-white/10 rounded-lg p-3">
                    {renderFields(eIdx, s.exercises[eIdx], true)}
                  </div>
                );
              }

              const groupType = s.exercises[block.indices[0]].set_type;
              const groupRest = s.exercises[block.indices[0]].rest_seconds;
              const lastIdx = block.indices[block.indices.length - 1];
              return (
                <div key={block.groupId} className="bg-amber-500/[0.04] border-2 border-amber-500/30 rounded-lg p-3 space-y-3" data-testid={`${testIdPrefix}group-block-${sIdx}-${block.groupId}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-black uppercase tracking-wide px-2 py-1 rounded-full bg-amber-500/20 text-amber-300 inline-flex items-center gap-1">
                      <Link2 className="w-3 h-3" /> {block.indices.length} exercices liés
                    </span>
                    <select value={groupType} onChange={(e) => setGroupType(sIdx, block.groupId, e.target.value)}
                      className="bg-zinc-800 border border-amber-500/30 rounded-lg px-2 py-1.5 text-amber-200 text-xs font-semibold" data-testid={`${testIdPrefix}group-type-select-${sIdx}-${block.groupId}`}>
                      {GROUP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <label className="text-[10px] text-zinc-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Récup après le bloc :
                      <select value={groupRest} onChange={(e) => setGroupRest(sIdx, block.groupId, +e.target.value)}
                        className="bg-zinc-800 border border-white/10 rounded-lg px-2 py-1 text-white text-xs" data-testid={`${testIdPrefix}group-rest-select-${sIdx}-${block.groupId}`}>
                        {REST_PRESETS.map((r) => <option key={r} value={r}>{r}s</option>)}
                      </select>
                    </label>
                    <button onClick={() => unlinkGroup(sIdx, block.groupId)} className="ml-auto inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white" data-testid={`${testIdPrefix}unlink-group-btn-${sIdx}-${block.groupId}`}>
                      <Unlink className="w-3.5 h-3.5" /> Dissocier
                    </button>
                  </div>
                  {block.indices.map((eIdx, mi) => (
                    <div key={eIdx} className={mi > 0 ? 'pt-3 border-t border-amber-500/10' : ''}>
                      {renderFields(eIdx, s.exercises[eIdx], false)}
                    </div>
                  ))}
                  <button onClick={() => addExerciseToGroup(sIdx, lastIdx, block.groupId, groupType, groupRest)} className="text-xs text-amber-300 hover:underline inline-flex items-center gap-1" data-testid={`${testIdPrefix}add-to-group-btn-${sIdx}-${block.groupId}`}>
                    <Plus className="w-3.5 h-3.5" /> Ajouter un exercice à ce bloc
                  </button>
                </div>
              );
            })}
            <button onClick={() => upd((c) => { c.sessions[sIdx].exercises.push(blankExercise()); })} className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid={`${testIdPrefix}add-exercise-btn-${sIdx}`}>
              <Plus className="w-3.5 h-3.5" /> Ajouter un exercice
            </button>
          </div>
        </div>
      ))}

      <button onClick={() => upd((c) => { c.sessions.push(blankSession(c.sessions.length + 1)); })} className="text-sm text-primary hover:underline inline-flex items-center gap-1.5" data-testid={`${testIdPrefix}add-session-btn`}>
        <Plus className="w-4 h-4" /> Ajouter une séance type
      </button>

      <div className="flex items-center gap-3 pt-2 border-t border-white/10">
        <button onClick={onSave} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary to-pink-500 text-white font-bold rounded-xl hover:scale-105 transition-transform disabled:opacity-50" data-testid={`${testIdPrefix}save-program-pro-btn`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Enregistrer
        </button>
        <button onClick={onCancel} className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 text-zinc-300 rounded-xl hover:border-white/30 transition-colors" data-testid={`${testIdPrefix}cancel-program-pro-btn`}>
          <X className="w-4 h-4" /> Annuler
        </button>
      </div>

      {pickerFor && (
        <ExercisePickerModal
          initialQuery={editing.sessions[pickerFor.sIdx].exercises[pickerFor.eIdx].name}
          onClose={() => setPickerFor(null)}
          onSelect={(ex) => applyLibraryExercise(pickerFor.sIdx, pickerFor.eIdx, ex)}
          csrfToken={csrfToken}
        />
      )}
    </div>
  );
};

export default ProgramEditorForm;
